export const IMAGE_ROW_GAP = 12;
export const IMAGE_ROW_DEFAULT_WIDTH = 320;

export function estimateImageDisplayWidth(meta) {
  if (meta?.width) return meta.width;
  return IMAGE_ROW_DEFAULT_WIDTH;
}

function getLines(source) {
  return String(source || '').replace(/\r\n/g, '\n').split('\n');
}

function getImageMetaAt(source, lineIndex) {
  const lines = getLines(source);
  if (lineIndex < 0 || lineIndex >= lines.length) return null;
  return parseImageLine(lines[lineIndex].trim());
}

function readAttr(attrString, name) {
  const re = new RegExp(`${name}\\s*=\\s*["']([^"']*)["']`, 'i');
  const match = String(attrString || '').match(re);
  return match ? match[1] : '';
}

export function normalizeTutorialImageSrc(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  if (raw.startsWith('/uploads/') || raw.startsWith('/api/')) {
    return raw.split('?')[0];
  }
  try {
    const parsed = new URL(raw, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    if (parsed.pathname.startsWith('/uploads/') || parsed.pathname.startsWith('/api/storage/images/view')) {
      return `${parsed.pathname}${parsed.search}`;
    }
    return raw;
  } catch {
    return raw;
  }
}

export function parseImageLine(trimmed) {
  const line = String(trimmed || '').trim();
  if (!line) return null;

  const htmlMatch = line.match(/^<img\s+([^>]*?)\s*\/?>$/i);
  if (htmlMatch) {
    const attrs = htmlMatch[1];
    const widthRaw = readAttr(attrs, 'width');
    const heightRaw = readAttr(attrs, 'height');
    const width = widthRaw && widthRaw !== '100%' ? Number.parseInt(widthRaw, 10) || null : null;
    const height = heightRaw && heightRaw !== 'auto' ? Number.parseInt(heightRaw, 10) || null : null;
    const offsetXRaw = readAttr(attrs, 'data-offset-x');
    const offsetYRaw = readAttr(attrs, 'data-offset-y');
    const offsetX = offsetXRaw ? Number.parseInt(offsetXRaw, 10) || 0 : 0;
    const offsetY = offsetYRaw ? Number.parseInt(offsetYRaw, 10) || 0 : 0;
    const row = readAttr(attrs, 'data-row') || null;
    return {
      format: 'html',
      src: normalizeTutorialImageSrc(readAttr(attrs, 'src')),
      alt: readAttr(attrs, 'alt') || '',
      width,
      height,
      align: readAttr(attrs, 'data-align') || 'center',
      offsetX,
      offsetY,
      row,
    };
  }

  const mdMatch = line.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
  if (mdMatch) {
    let alt = mdMatch[1];
    let width = null;
    let align = 'center';
    const parts = alt.split('|');
    if (parts.length >= 3) {
      alt = parts[0];
      width = Number.parseInt(parts[1], 10) || null;
      align = parts[2] || 'center';
    }
    return {
      format: 'md',
      src: normalizeTutorialImageSrc(mdMatch[2]),
      alt,
      width,
      align,
    };
  }

  return null;
}

export function buildImageLine({
  src,
  alt = '',
  width = null,
  height = null,
  align = 'center',
  offsetX = 0,
  offsetY = 0,
  row = null,
}) {
  const safeSrc = normalizeTutorialImageSrc(src);
  const safeAlt = String(alt || '').replace(/"/g, "'");
  const widthAttr = width ? ` width="${width}"` : '';
  const heightAttr = height ? ` height="${height}"` : '';
  const alignAttr = ` data-align="${align || 'center'}"`;
  const offsetXAttr = offsetX ? ` data-offset-x="${offsetX}"` : '';
  const offsetYAttr = offsetY ? ` data-offset-y="${offsetY}"` : '';
  const rowAttr = row ? ` data-row="${row}"` : '';
  return `<img src="${safeSrc}" alt="${safeAlt}"${widthAttr}${heightAttr}${alignAttr}${offsetXAttr}${offsetYAttr}${rowAttr} />`;
}

export function findImageBlocks(source) {
  const lines = String(source || '').replace(/\r\n/g, '\n').split('\n');
  return lines.reduce((acc, line, lineIndex) => {
    const parsed = parseImageLine(line.trim());
    if (!parsed) return acc;
    acc.push({
      lineIndex,
      raw: line,
      ...parsed,
    });
    return acc;
  }, []);
}

export function updateImageBlock(source, lineIndex, meta) {
  const lines = getLines(source);
  if (lineIndex < 0 || lineIndex >= lines.length) return source;
  lines[lineIndex] = buildImageLine(meta);
  return lines.join('\n');
}

export function getPreviousImageLineIndex(source, lineIndex) {
  const blocks = findImageBlocks(source);
  const pos = blocks.findIndex((block) => block.lineIndex === lineIndex);
  if (pos <= 0) return null;
  return blocks[pos - 1].lineIndex;
}

export function calcRowTotalWidth(members) {
  return members.reduce(
    (sum, member, index) => sum + estimateImageDisplayWidth(member) + (index > 0 ? IMAGE_ROW_GAP : 0),
    0,
  );
}

export function joinImageWithPrevious(source, lineIndex, containerWidth) {
  const blocks = findImageBlocks(source);
  const pos = blocks.findIndex((block) => block.lineIndex === lineIndex);
  if (pos <= 0) {
    return { ok: false, message: '没有上一张图片' };
  }

  const prev = blocks[pos - 1];
  const current = blocks[pos];
  const rowId = prev.row || `row-${prev.lineIndex}`;
  const members = prev.row
    ? blocks.filter((block) => block.row === prev.row)
    : [prev];

  if (!members.some((block) => block.lineIndex === lineIndex)) {
    members.push(current);
  }

  const totalWidth = calcRowTotalWidth(members);
  const available = Math.max(containerWidth, 0);
  if (totalWidth > available) {
    return {
      ok: false,
      message: `当前行宽度不足：并排约需 ${totalWidth}px，可用 ${Math.floor(available)}px，请缩小图片宽度后再试`,
    };
  }

  let next = source;
  members.forEach((member) => {
    const meta = getImageMetaAt(next, member.lineIndex);
    if (!meta) return;
    next = updateImageBlock(next, member.lineIndex, { ...meta, row: rowId });
  });

  return { ok: true, content: next };
}

export function setImageStandalone(source, lineIndex) {
  const meta = getImageMetaAt(source, lineIndex);
  if (!meta) return { ok: false, message: '图片不存在' };
  if (!meta.row) return { ok: true, content: source };

  const rowId = meta.row;
  let next = updateImageBlock(source, lineIndex, { ...meta, row: null });
  const remaining = findImageBlocks(next).filter((block) => block.row === rowId);
  if (remaining.length === 1) {
    const loneMeta = getImageMetaAt(next, remaining[0].lineIndex);
    if (loneMeta) {
      next = updateImageBlock(next, remaining[0].lineIndex, { ...loneMeta, row: null });
    }
  }

  return { ok: true, content: next };
}

export function validateImageRowWidth(source, lineIndex, containerWidth) {
  const meta = getImageMetaAt(source, lineIndex);
  if (!meta?.row) return { ok: true };
  const members = findImageBlocks(source).filter((block) => block.row === meta.row);
  const totalWidth = calcRowTotalWidth(members);
  const available = Math.max(containerWidth, 0);
  if (totalWidth > available) {
    return {
      ok: false,
      message: `同行图片宽度约 ${totalWidth}px，超出可用 ${Math.floor(available)}px，请缩小宽度或改为独立成行`,
    };
  }
  return { ok: true };
}

export function normalizeLegacyImageContent(source) {
  const lines = String(source || '').replace(/\r\n/g, '\n').split('\n');
  let changed = false;
  const next = lines.map((line) => {
    const trimmed = line.trim();
    const parsed = parseImageLine(trimmed);
    if (!parsed || parsed.format === 'html') return line;
    changed = true;
    return buildImageLine(parsed);
  });
  return changed ? next.join('\n') : source;
}
